import React from 'react';
import { motion } from 'framer-motion';
import { FileText, TrendingUp, Clock, AlertCircle, CheckCircle2, Calendar } from 'lucide-react';
import QuoteBookingAnalyticsComponent from '../components/dashboard/QuoteBookingAnalytics';
import BookingOperationsComponent from '../components/dashboard/BookingOperations';

const QuotesBookings = () => {
  const quoteMetrics = [
    { label: 'Active Quotes', value: '42', change: '+15%', color: 'from-blue-500 to-blue-600' },
    { label: 'Conversion Rate', value: '68%', change: '+5%', color: 'from-green-500 to-green-600' },
    { label: 'Pending Bookings', value: '18', change: '+3', color: 'from-orange-500 to-orange-600' },
    { label: 'Confirmed Today', value: '12', change: '+4', color: 'from-purple-500 to-purple-600' },
  ];

  const recentQuotes = [
    { id: 'QT-2025-001', customer: 'Global Trading Co.', origin: 'Shanghai', dest: 'Los Angeles', status: 'pending', expires: '2 days' },
    { id: 'QT-2025-002', customer: 'Pacific Imports', origin: 'Singapore', dest: 'Rotterdam', status: 'accepted', expires: '-' },
    { id: 'QT-2025-003', customer: 'Ocean Freight Ltd', origin: 'Mumbai', dest: 'New York', status: 'pending', expires: '5 days' },
    { id: 'QT-2025-004', customer: 'Asia Connect', origin: 'Hong Kong', dest: 'Hamburg', status: 'expired', expires: '-2 days' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0A34A0] to-[#0072FF] rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center space-x-3 mb-3">
          <FileText className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Quotes & Bookings</h1>
        </div>
        <p className="text-white/90 text-lg">
          Manage quotes, track conversions, and streamline booking operations
        </p>
      </div>

      {/* Quote Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {quoteMetrics.map((metric, index) => (
          <motion.div
            key={index}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white`}
          >
            <h3 className="text-sm font-medium opacity-80 mb-2">{metric.label}</h3>
            <p className="text-3xl font-bold mb-1">{metric.value}</p>
            <p className="text-sm opacity-80">{metric.change} from last week</p>
          </motion.div>
        ))}
      </div>

      {/* Analytics Components */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuoteBookingAnalyticsComponent />
        <BookingOperationsComponent />
      </div>

      {/* Recent Quotes */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
      >
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-[#0072FF]" />
          Recent Quotes
        </h3>
        <div className="space-y-3">
          {recentQuotes.map((quote, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <div className="flex items-center space-x-4 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  quote.status === 'pending' ? 'bg-orange-100 dark:bg-orange-900/30' :
                  quote.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/30' :
                  'bg-red-100 dark:bg-red-900/30'
                }`}>
                  {quote.status === 'pending' ? (
                    <Clock className="w-5 h-5 text-orange-600" />
                  ) : quote.status === 'accepted' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 dark:text-white">{quote.id}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">{quote.customer}</div>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {quote.origin} → {quote.dest}
                </div>
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    quote.status === 'pending' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                    quote.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  }`}>
                    {quote.status.toUpperCase()}
                  </span>
                </div>
                {quote.expires !== '-' && (
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Expires: {quote.expires}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QuotesBookings;
